
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { supportService } from '../../services/api';

interface SupportForm {
    subject: string;
    priority: string;
    message: string;
}

export default function Support() {
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<SupportForm>();
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [ticketId, setTicketId] = useState<string | null>(null);

    const onSubmit = async (data: SupportForm) => {
        setStatus('idle');
        setTicketId(null);
        try {
            const res = await supportService.createTicket(data);
            setTicketId(res.data?.id || '12345'); // Fallback if API doesn't return ID immediately
            setStatus('success');
            reset();
        } catch (error) {
            console.error("Failed to submit ticket", error);
            setStatus('error');
        }
    };

    const faqItems = [
        { question: "How do I upgrade my plan?", answer: "Go to Settings > Subscription and click 'Upgrade' or 'Manage Billing'." },
        { question: "How do I add a new property?", answer: "Click 'Add Property' from the Dashboard or Properties page to start the wizard." },
        { question: "What file types are supported for certificates?", answer: "We support PDF, JPG, and PNG files up to 5MB." },
        { question: "Can I delete a property?", answer: "Yes, navigate to the property details page and select 'Delete Property' from the actions menu." },
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8 text-center">
                    <h2 className="text-3xl font-extrabold text-gray-900">How can we help you?</h2>
                    <p className="mt-2 text-lg text-gray-500">Search our knowledge base or submit a support ticket.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                    {/* Left Column: FAQ & Knowledge Base */}
                    <div>
                        <div className="bg-white shadow rounded-lg p-6 mb-8">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Frequently Asked Questions</h3>
                            <div className="space-y-6">
                                {faqItems.map((item, idx) => (
                                    <div key={idx}>
                                        <dt className="text-base font-medium text-gray-900">{item.question}</dt>
                                        <dd className="mt-2 text-sm text-gray-600">{item.answer}</dd>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex items-start">
                            <div className="flex-shrink-0">
                                <i className="fas fa-envelope-open-text text-blue-400 text-2xl"></i>
                            </div>
                            <div className="ml-4">
                                <h4 className="text-base font-medium text-blue-900">Need direct email support?</h4>
                                <p className="mt-1 text-sm text-blue-700">
                                    You can also reach us at <a href="mailto:hello@compliance-engine.io" className="underline font-medium">hello@compliance-engine.io</a> for urgent inquiries.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Contact Form */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-6">Submit a Request</h3>

                        {status === 'success' ? (
                            <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                                <i className="fas fa-check-circle text-green-500 text-3xl mb-3"></i>
                                <h4 className="text-lg font-medium text-green-800">Ticket Submitted!</h4>
                                <p className="text-sm text-green-600 mt-1">
                                    Your ticket #{ticketId} has been created. We'll be in touch shortly.
                                </p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-4 text-sm font-medium text-green-700 hover:text-green-600 underline"
                                >
                                    Submit another request
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Subject / Category</label>
                                    <select
                                        {...register('subject', { required: 'Please select a subject' })}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md border"
                                    >
                                        <option value="">Select a topic...</option>
                                        <option value="Billing">Billing & Subscription</option>
                                        <option value="Technical">Technical Issue / Bug</option>
                                        <option value="Feature">Feature Request</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                                    <div className="mt-2 space-x-4 flex">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                value="Low"
                                                {...register('priority', { required: true })}
                                                defaultChecked
                                                className="form-radio text-green-600 focus:ring-green-500"
                                            />
                                            <span className="ml-2 text-gray-700 text-sm">Low</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                value="Medium"
                                                {...register('priority', { required: true })}
                                                className="form-radio text-yellow-500 focus:ring-yellow-500"
                                            />
                                            <span className="ml-2 text-gray-700 text-sm">Medium</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                value="High"
                                                {...register('priority', { required: true })}
                                                className="form-radio text-red-600 focus:ring-red-500"
                                            />
                                            <span className="ml-2 text-gray-700 text-sm font-medium">High</span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Message</label>
                                    <textarea
                                        rows={5}
                                        {...register('message', {
                                            required: 'Please describe your issue',
                                            minLength: { value: 20, message: 'Please provide at least 20 characters' }
                                        })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                        placeholder="Describe the issue you are facing..."
                                    />
                                    {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>}
                                </div>

                                {status === 'error' && (
                                    <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md">
                                        Could not submit ticket. Please try again or email us.
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Sending...' : 'Submit Request'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    );
}
